import React from 'react';
import PropTypes from 'prop-types';
import { Badge, Flyout, Icon } from '@statsbomb/kitbag-components';
import {
  TRANCHE_MODE,
  TRANCHE_STATUS,
  getAccessibleName,
  getBadgeVariant,
  getLabel,
  getTranches,
} from './TrancheStatus.dataManipulation';
import {
  BadgeButton,
  BadgeContent,
  Dot,
  DotsRow,
  FlyoutCornerStyles,
  IconWrapper,
  Panel,
  StatusText,
  TrancheDescription,
  TrancheInfo,
  TrancheRow,
  TrancheTitle,
} from './TrancheStatus.styles';
import useTranchePanel from './useTranchePanel';

const STATUS_ICON = {
  [TRANCHE_STATUS.AVAILABLE]: 'Success',
  [TRANCHE_STATUS.ERROR]: 'Warning',
};

// An unfilled circle the size of the Success icon's circle (radius 8 on the
// 24-unit grid). The 1.5 stroke renders as 1px at 1rem. The colour comes from
// IconWrapper via currentColor.
function PendingIcon() {
  return (
    <svg
      width="1rem"
      height="1rem"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="7.25" />
    </svg>
  );
}

const STATUS_LABEL = {
  [TRANCHE_STATUS.AVAILABLE]: 'Available',
  [TRANCHE_STATUS.PENDING]: 'Pending',
  [TRANCHE_STATUS.ERROR]: 'Error',
};

function TrancheStatus({ mode, statuses, defaultOpen }) {
  const { isOpen, triggerPosition, handleOutsideClick, buttonProps, panelProps } =
    useTranchePanel(defaultOpen);

  if (!statuses) return null;
  const label = getLabel(mode, statuses);
  const variant = getBadgeVariant(mode, statuses);
  const tranches = getTranches(mode, statuses);
  const accessibleName = getAccessibleName(mode, statuses);

  return (
    <>
      <BadgeButton
        {...buttonProps}
        type="button"
        aria-label={accessibleName}
        data-testid="tranche-status-badge"
      >
        <Badge variant={variant} hasIcon={false}>
          <BadgeContent>
            <DotsRow>
              {tranches.map((tranche) => (
                <Dot
                  key={tranche.key}
                  $filled={tranche.status === TRANCHE_STATUS.AVAILABLE}
                />
              ))}
            </DotsRow>
            {label}
            <Icon variant="Info" size="tiny" />
          </BadgeContent>
        </Badge>
      </BadgeButton>
      {isOpen && (
        <Flyout
          placement="bottom-end"
          triggerPosition={triggerPosition}
          onOutsideClick={handleOutsideClick}
          disableScrollWhenOpen={false}
          isPortalAlwaysOnScreen
          // Flyout does arithmetic on offset, so it needs pixels: 8 matches
          // Uniform's --u-space-half and theme.sizing.small.
          offset={{ top: 8, left: 0 }}
          testId="tranche-status-flyout"
        >
          <FlyoutCornerStyles />
          <Panel
            {...panelProps}
            role="region"
            aria-label="Data tranches"
            data-testid="tranche-status-panel"
          >
            {tranches.map((tranche) => (
              <TrancheRow key={tranche.key}>
                <IconWrapper $status={tranche.status} aria-hidden="true">
                  {tranche.status === TRANCHE_STATUS.PENDING ? (
                    <PendingIcon />
                  ) : (
                    <Icon variant={STATUS_ICON[tranche.status]} size="small" />
                  )}
                </IconWrapper>
                <TrancheInfo>
                  <TrancheTitle>{tranche.title}</TrancheTitle>
                  <TrancheDescription>{tranche.description}</TrancheDescription>
                </TrancheInfo>
                <StatusText $status={tranche.status}>
                  {STATUS_LABEL[tranche.status]}
                </StatusText>
              </TrancheRow>
            ))}
          </Panel>
        </Flyout>
      )}
    </>
  );
}

TrancheStatus.propTypes = {
  mode: PropTypes.oneOf(Object.values(TRANCHE_MODE)).isRequired,
  statuses: PropTypes.shape({
    core: PropTypes.oneOf(Object.values(TRANCHE_STATUS)),
    tracking: PropTypes.oneOf(Object.values(TRANCHE_STATUS)),
    advanced: PropTypes.oneOf(Object.values(TRANCHE_STATUS)),
  }),
  defaultOpen: PropTypes.bool,
};

TrancheStatus.defaultProps = {
  statuses: null,
  defaultOpen: false,
};

export default TrancheStatus;
